import {
  Alert,
  Box,
  Button,
  Radio as ChakraRadio, RadioGroup as ChakraRadioGroup,
  Checkbox,
  CheckboxGroup,
  FormControl,
  HStack,
  Link,
  Select,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { Error, SimpleItem } from 'BrokerTsLib/src/dtos'
import { DiscountType } from 'BrokerTsLib/src/dtos'
import {
  ContractAcceptationType,
  Insurer,
  PaymentNotificationTypeValues,
  SelectionType
} from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { Controller, FormProvider, useFieldArray, useForm } from 'BrokerTsLib/src/rhf'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { getConsequenceTexts, getReasonTexts } from '@/utils/meetingRecordUtils'
import { MeetingRecordCheckboxesFormControl } from 'BrokerTsLib/src/components/forms/MeetingRecordCheckboxesFormControl'
import { Selection } from 'BrokerTsLib/src/enums'
import ErrorSummary from '../components/ErrorSummary'
import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionHeader,
  FormSectionTitle,
} from '../components/FormSection'
import { InputGroup } from '../components/InputGroup'
import { Loading } from '../components/Loading'
import { MainContent } from '../components/MainContent'
import { Radio } from '../components/Radio'
import { RadioStack } from '../components/RadioStack'
import { TextareaGroup } from '../components/TextareaGroup'
import { useAuth } from '../context/AuthContext'
import type { OkAppsMeetingRecord } from '../dtos/OkAppsMeetingRecord'
import { baseUrl, useSelections } from '../hooks'
import { useApp } from '../hooks/useApp'
import { useGetAllDocuments } from '../hooks/useDocuments'
import { useGetAvailableBusinessmen, useUrl as useInsuranceParamsUrl } from '../hooks/useInsuranceParams'
import { useCreateContract, useSaveMeetingRecord } from '../hooks/useWizard'
import { Check, Duplicate } from '../icons'
import { insuranceParamsContext } from '../layouts/Wizard'
import { meetingRecordContext } from '../pages/carkulka/wizard/5'
import { FormButtons } from './FormButtons'
import { insurerOfferContext } from './hoc/WithOffer'
import { PreviousContractNumberForm } from './PreviousContractNumberForm'

export const AvailableCustomerKnowledge: SimpleItem[] = [
  { id: 0, name: 'Zákazník NEMÁ dostatečné znalosti a zkušenosti' },
  { id: 1, name: 'Zákazník již měl sjednáno pojištění s obdobným rozsahem pojistného krytí' },
  { id: 2, name: 'Zákazník MÁ znalosti z oboru pojišťovnictví' },
]


export const MeetingRecordForm = () => {
  const { app, product } = useApp()
  const { user } = useAuth() ?? {}
  const { insuranceParamsId, insuranceParams, refetchInsuranceParams } =
    useContext(insuranceParamsContext)
  const [acceptChangedPrice, setAcceptChangedPrice] = useState(false)
  const [priceChanged, setPriceChanged] = useState(false)
  const [returnBack, setReturnBack] = useState(true)
  const [alerts, setAlerts] = useState<Error[]>([])
  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const {
    isLoading: isLoadingMR,
    refetch: refetchMR,
    meetingRecord,
  } = useContext(meetingRecordContext)
  const { offer, refetch: refetchOffer } = useContext(insurerOfferContext)

  const { refetch: refetchDocuments } = useGetAllDocuments(
    insuranceParams!.generalParams.paramsId!,
    insuranceParams!.generalParams.selectedOfferId!,
    { enabled: false },
  )
  const url = useInsuranceParamsUrl()

  const { data: dataBusinessmen } = useGetAvailableBusinessmen(insuranceParamsId!)

  // save formulare
  const { mutateAsync: saveMeetingRecord } = useSaveMeetingRecord()

  // sjednani smlouvy
  const { mutateAsync: createContract } = useCreateContract()

  // reset formulare po nacteni dat
  useEffect(() => {
    if (meetingRecord) reset(meetingRecord)
  }, [!meetingRecord])

  const methods = useForm<OkAppsMeetingRecord>({
    defaultValues: meetingRecord,
  })
  const {
    control,
    handleSubmit,
    register,
    getValues,
    setValue,
    reset,
    setError,
    watch,
    trigger,
    formState: { errors, isSubmitting },
    getFieldState,
  } = methods

  const isValid = !Object.keys(errors).length

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const { data: dataCat } = useSelections(SelectionType.FormaSjednani)
  const { data: dataFirstPaymentType } = useSelections(SelectionType.ZpusobPrvniPlatby)
  const { data: dataDigitalSignatureType } = useSelections(SelectionType.ZpusobDigitalnihoPodpisu)
  const { fields } = useFieldArray({ control, name: 'brokerNumbers' })

  let enabledAcceptTypesCount = 0
  if (meetingRecord?.paymentEnabled) enabledAcceptTypesCount++
  if (meetingRecord?.signatureEnabled) enabledAcceptTypesCount++
  if (meetingRecord?.digitalSignatureEnabled) enabledAcceptTypesCount++

  // zobrazeni validace nabidky ve validation summary (zobrazuje se uz pri getu)
  useEffect(() => {
    if (meetingRecord?.errors)
      meetingRecord.errors.forEach((x) => setError('' as any, { message: x.message }))
  }, [meetingRecord?.errors])

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const saveAndCreateContract = (values: OkAppsMeetingRecord) => {
    return new Promise((resolve, reject) => {
      if (!values.hasMoreContracts) values.hasMoreContracts = false // pokud se nezmění výchozí hodnota (undefined), je třeba nastavit na false
      if (!values.refusesMoreDetails) values.refusesMoreDetails = false // pokud se nezmění výchozí hodnota (undefined), je třeba nastavit na false

      return saveMeetingRecord(values)
        .then((resp) => {
          // v pripade pouze ulozeni jenom refreshneme formular
          if (values.nextPage == 'current') {
            savedFormAlertOnOpen()
            refetchMR()
            window.scrollTo(0, 0)

            // pokud nesjednavam pojistku, tak nepokracuji
            resolve(resp)
            return
          }

          // sjednani pojistky
          createContract({ paramsId: insuranceParamsId!, acceptChangedPrice: acceptChangedPrice })
            .then((resp) => {
              resolve(resp)

              if (resp.data.priceChanged && !resp.data.contractCreated) {
                setPriceChanged(true)
                setReturnBack(resp.data.returnBack)
                if (resp.data.errors.length > 0) setAlerts(resp.data.errors)
              } else if (resp.data.errors.length > 0) setAlerts(resp.data.errors)
              else {
                refetchInsuranceParams()
                refetchMR()
                refetchOffer()

                window.scrollTo(0, 0)

                refetchDocuments()
              }
            })
            .catch((err: AxiosError) => {
              reject(err)
            })
        })
        .catch((err) => {
          reject(err)
        })
    })
  }

  const submitForm = handleSubmit((values) => {
    return saveAndCreateContract(values).catch((err) => {
      if (!(err.response && err.response.status === 400 && err.response.data)) return

      const data = err.response.data

      // zobrazeni serverove validace
      Object.keys(data).forEach((x: any) => {
        setError(global.toCamelCase(x), { message: data[x].join(' ') })
      })
    })
  })

  useEffect(() => {
    if (isSubmittingNext || isSubmittingCurrent) submitForm()
  }, [isSubmittingNext, isSubmittingCurrent])

  const pcn = getValues('previousContractNumber')
  const contractArrangedRemotelyEnabled = getValues('contractArrangedRemotelyEnabled')

  const actStartType = watch('startType')
  
      const sameAcquirerAndPolicyHolder = useMemo(() => {
          if (insuranceParams?.policyHolder.person.type == Selection.Person) {
              const currentUserKey = `${user?.email},${user?.firstName},${user?.lastName}`
              const policyHolderKey = `${insuranceParams?.policyHolder.person.email},${insuranceParams?.policyHolder.person.name},${insuranceParams?.policyHolder.person.surname}`
  
              if (currentUserKey === policyHolderKey)
                  return true
          }
          else {
              if (user?.ico == insuranceParams?.policyHolder.person.idendificationNumber)
                  return true
          }
      }, [user, insuranceParams?.policyHolder.person.idendificationNumber, insuranceParams?.policyHolder.person.type, insuranceParams?.policyHolder.person.email, insuranceParams?.policyHolder.person.name, insuranceParams?.policyHolder.person.surname])

  const allowCreateContract = (user?.insuringAllowed ?? 0) == 1 && !sameAcquirerAndPolicyHolder

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Záznam z jednání</FormSectionTitle>
            </FormSectionHeader>

            {!meetingRecord && isLoadingMR && <Loading large={true} />}
            {meetingRecord && (
              <FormSectionBody sx={{ gap: 8 }}>
                <FormControl variant="fullWidth">
                  <FormLabel
                    optional
                    tooltip="Uveďte nesrovnalosti mezi nabízeným pojištěním a požadavky zákazníka. Tyto údaje se propíšou do Záznamu z jednání, který zákazník podepisuje."
                  >
                    Případné nesrovnalosti mezi požadavky Zákazníka a sjednávaným pojištěním nebo
                    jeho podstatnou změnou
                  </FormLabel>

                  <TextareaGroup
                    placeholder="Uveďte nesrovnalosti mezi nabízeným pojištěním a požadavky zákazníka. Tyto údaje se propíšou do Záznamu z jednání, které zákazník podepisuje."
                    errors={errors.irregularities}
                    {...register('irregularities')}
                  />
                </FormControl>

                <MeetingRecordCheckboxesFormControl
                  name='reasons'
                  checkboxTexts={getReasonTexts(product!)}
                  label='Důvody, na kterých je doporučení založeno'
                  tooltip="Po vyhodnocení výše uvedených požadavků a potřeb Zákazníka v požadovaných oblastech pojištění a při zohlednění jeho znalostí, zkušeností a finanční situace doporučuje Zprostředkovatel následující produkty. Toto doporučení je založeno pouze na vyhodnocení produktů, které je Zprostředkovatel a jeho zástupce oprávněn zprostředkovávat. Můžete vybrat předpřipravenou textaci důvodu, po vybrání můžete doplnit vlastní text."
                  hintType='okapps'
                  isRequired
                />

                <MeetingRecordCheckboxesFormControl
                  name='consequences'
                  checkboxTexts={getConsequenceTexts(product!)}
                  label='Popis dopadů sjednání/změny/ukončení pojištění, včetně souvisejících rizik'
                  tooltip='V případě doporučení sjednání nové pojistné smlouvy nebo její podstatné změny je třeba vysvětlit dopady takového jednání vč. souvisejících rizik u každého sjednaného pojistného produktu.'
                  hintType='okapps'
                  helperText='V případě doporučení sjednání nové pojistné smlouvy nebo její podstatné změny je
                    třeba vysvětlit dopady takového jednání vč. souvisejících rizik u každého
                    sjednaného pojistného produktu.'
                  isRequired />

                <FormControl>
                  <FormLabel tooltip="V souvislosti s tím Zprostředkovatel upozorňuje Zákazníka, že byl případnou chybějící informací ovlivněn při doporučení pojistného produktu a nastavení vhodných pojistných částek/limitů poj. plnění u požadovaných nebezpečí a výše pojistného.">
                    Zákazník odmítá sdělit další požadavky?
                  </FormLabel>

                  <Controller
                    name="refusesMoreDetails"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ChakraRadioGroup
                        onChange={(e) => onChange(e == '1')}
                        value={value ? '1' : '0'}
                      >
                        <RadioStack variant="stackedTile">
                          <ChakraRadio value={'1'}>Ano</ChakraRadio>
                          <ChakraRadio value={'0'}>Ne</ChakraRadio>
                        </RadioStack>
                      </ChakraRadioGroup>
                    )}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel tooltip="Má zákazník vedle požadované/měněné pojistné smlouvy uzavřenou další pojistnou smlouvu, která kryje stejné pojistné nebezpečí?">
                    Má zákazník vedle této pojistné smlouvy uzavřenou další se stejným krytím?
                  </FormLabel>

                  <Controller
                    name="hasMoreContracts"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ChakraRadioGroup
                        onChange={(e) => onChange(e == '1')}
                        value={value ? '1' : '0'}
                      >
                        <RadioStack variant="stackedTile">
                          <ChakraRadio value={'1'}>Ano</ChakraRadio>
                          <ChakraRadio value={'0'}>Ne</ChakraRadio>
                        </RadioStack>
                      </ChakraRadioGroup>
                    )}
                  />
                </FormControl>

                {/* <FormControl>
                  <FormLabel tooltip="Jaké má zákazník znalosti produktů?">
                    Jaké má zákazník znalosti produktů?
                  </FormLabel>

                  <Controller
                    name="customerKnowledge"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ChakraRadioGroup onChange={e => onChange(parseInt(e))} value={value?.toString() ?? ''}>
                        <RadioStack variant='simple' sx={{ gridAutoFlow: 'row' }}>
                          {AvailableCustomerKnowledge.map(item => <ChakraRadio key={item.id} value={item.id.toString()}>{item.name}</ChakraRadio>)}
                        </RadioStack>
                      </ChakraRadioGroup>
                    )}
                  />
                </FormControl> */}

                <Controller
                  name="noCollison"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CheckboxGroup
                      onChange={(e) => onChange(e[0] == '1')}
                      value={value ? ['1'] : undefined}
                    >
                      <Checkbox value={'1'}>
                        Zákazník ani Zprostředkovatel si nejsou vědomi žádných nesrovnalostí mezi
                        požadavky Zákazníka a nabízeným pojištěním.
                      </Checkbox>
                    </CheckboxGroup>
                  )}
                />

                <FormControl isInvalid={!!errors.placeOfContract?.message} isRequired>
                  <FormLabel>Místo sjednání</FormLabel>
                  <InputGroup
                    placeholder="Např. Brno"
                    {...register('placeOfContract')}
                    errors={errors.placeOfContract}
                  />
                </FormControl>

                {offer?.insurerId == Insurer.Allianz &&
                  meetingRecord.discounts
                    ?.filter(
                      (x) =>
                        x.insuranceCategoryDiscount.discountType == DiscountType.Voucher &&
                        (x.discount ?? 0) > 0,
                    )
                    .map((x, i) => (
                      <FormControl
                        isInvalid={!!getFieldState(`discounts.${i}.voucherCode` as any).error}
                        key={x.insuranceCategoryDiscountId}
                      >
                        <FormLabel>
                          {x.insuranceCategoryDiscount.name} ({x.discount}%)
                        </FormLabel>
                        <InputGroup
                          {...register(`discounts[${i}].voucherCode` as any)}
                          errors={getFieldState(`discounts.${i}.voucherCode` as any).error}
                        />
                      </FormControl>
                    ))}

                <FormGroup>
                  <FormControl>
                    <FormLabel tooltip="Druh smlouvy">Druh smlouvy</FormLabel>
                    <ChakraRadioGroup>
                      <RadioStack variant="stackedTile">
                        <Radio isDisabled isChecked={!pcn}>
                          Nová smlouva
                        </Radio>
                        <Radio isDisabled isChecked={!!pcn}>
                          Náhrada stávající smlouvy
                        </Radio>
                      </RadioStack>
                    </ChakraRadioGroup>
                  </FormControl>

                  <PreviousContractNumberForm></PreviousContractNumberForm>
                </FormGroup>

                <FormGroup>
                  <FormControl isRequired isInvalid={!!errors.startType}>
                    <FormLabel>Způsob sjednání</FormLabel>

                    <Controller
                      name="startType"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <ChakraRadioGroup
                          onChange={(e) => onChange(parseInt(e))}
                          value={value?.toString()}
                          isDisabled={enabledAcceptTypesCount < 2}
                        >
                          <RadioStack variant="stackedTile">
                            {dataCat?.map((item) => (
                              <ChakraRadio
                                value={item.value}
                                key={item.id}
                                isDisabled={
                                  (!meetingRecord.paymentEnabled &&
                                    Number(item.value) == ContractAcceptationType.Payment) ||
                                  (!meetingRecord.signatureEnabled &&
                                    Number(item.value) == ContractAcceptationType.Signature) ||
                                  (!meetingRecord.digitalSignatureEnabled &&
                                    Number(item.value) == ContractAcceptationType.DigitalSignature)
                                }
                              >
                                {item.description}
                              </ChakraRadio>
                            ))}
                          </RadioStack>
                        </ChakraRadioGroup>
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel tooltip="Výběrem této možnosti se odešle upozornění na nutnost zaplacení prvního pojistného. CARkulka nijak nekontroluje zaplacenost pojistného, tento e-mail se tedy odešle i v případě, že klient již pojistné uhradil.">
                      Upozornění na blížící se datum splatnosti prvního pojistného
                    </FormLabel>

                    <Controller
                      name="contractExpiryWarning"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CheckboxGroup
                          onChange={(e) => onChange(e.map((item) => parseInt(item.toString())))}
                          value={value}
                        >
                          <HStack>
                            <Checkbox value={PaymentNotificationTypeValues.Broker}>
                              Sjednatel
                            </Checkbox>
                            <Checkbox value={PaymentNotificationTypeValues.Client}>
                              Zákazník
                            </Checkbox>
                          </HStack>
                        </CheckboxGroup>
                      )}
                    />
                  </FormControl>
                </FormGroup>

                {(meetingRecord.firstPaymentTypePaymentGateEnabled ||
                  meetingRecord.firstPaymentTypeBankTransferEnabled) &&
                  actStartType == 2 && (
                    <FormGroup>
                      <FormControl isRequired isInvalid={!!errors.firstPaymentType?.message}>
                        <FormLabel>Způsob první platby</FormLabel>

                        <Controller
                          name="firstPaymentType"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <ChakraRadioGroup
                              onChange={(e) => onChange(parseInt(e))}
                              value={value?.toString()}
                            >
                              <RadioStack variant="stackedTile">
                                {dataFirstPaymentType?.map((item) => (
                                  <ChakraRadio value={item.id.toString()} key={item.id}
                                    isDisabled={(item.name === 'PaymentGate' && !meetingRecord.firstPaymentTypePaymentGateEnabled) || (item.name === 'Bank' && !meetingRecord.firstPaymentTypeBankTransferEnabled)}
                                  >
                                    {item.description}
                                  </ChakraRadio>
                                ))}
                              </RadioStack>
                            </ChakraRadioGroup>
                          )}
                        />
                      </FormControl>
                    </FormGroup>
                  )}

                {dataDigitalSignatureType &&
                  dataDigitalSignatureType.length > 0 &&
                  actStartType == ContractAcceptationType.DigitalSignature && (
                    <FormGroup>
                      <FormControl isRequired isInvalid={!!errors.digitalSignatureType?.message}>
                        <FormLabel>Způsob digitálního podpisu</FormLabel>

                        <Controller
                          name="digitalSignatureType"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <ChakraRadioGroup
                              onChange={(e) => onChange(parseInt(e))}
                              value={value?.toString()}
                              isDisabled={(meetingRecord.allowedDigitalSignatureTypes?.length ?? 0) <= 1}
                            >
                              <RadioStack variant="stackedTile">
                                {dataDigitalSignatureType
                                  ?.map((item) => (
                                    <ChakraRadio value={item.id.toString()} key={item.id} isDisabled={(meetingRecord.allowedDigitalSignatureTypes?.indexOf(item.id) ?? -1) === -1}>
                                      {item.description}
                                    </ChakraRadio>
                                  ))}
                              </RadioStack>
                            </ChakraRadioGroup>
                          )}
                        />
                      </FormControl>
                    </FormGroup>
                  )}

                {contractArrangedRemotelyEnabled && (
                  <FormGroup>
                    <FormControl isRequired>
                      <FormLabel>Smlouva je sjednána</FormLabel>

                      <Controller
                        name="isContractArrangedRemotely"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <ChakraRadioGroup
                            onChange={(e) => onChange(e == '1')}
                            value={value ? '1' : '0'}
                          >
                            <RadioStack variant="stackedTile">
                              <ChakraRadio value={'0'}>Osobně</ChakraRadio>
                              <ChakraRadio value={'1'}>Na dálku</ChakraRadio>
                            </RadioStack>
                          </ChakraRadioGroup>
                        )}
                      />
                    </FormControl>
                  </FormGroup>
                )}

                {dataBusinessmen?.data &&
                  dataBusinessmen?.data.length > 0 &&
                  user!.allowCreateInsuranceAsSomeoneElse && (
                    <FormControl>
                      <FormLabel tooltip="Slouží pro propsání klienta a smlouvy pod jiného získatele/správce dle výběru níže. V případě, že chcete doplnit další osobu obraťte se s žádostí na carkulka@okholding.cz">
                        Sjednání za jiného uživatele
                      </FormLabel>

                      <Box sx={{ display: 'grid', gap: 4 }}>
                        <HStack>
                          <FormLabel tooltip="Klient bude zapsán pod vybraným získatelem. V případě, že tyto hodnoty měnit nechcete, ponechejte pole prázdné.">
                            Ziskatel
                          </FormLabel>
                          <Select
                            id="businessmanId"
                            {...register('businessmanId')}
                            defaultValue={getValues('businessmanId') ?? ''}
                          >
                            <option value="">Vyberte ziskatele</option>

                            {dataBusinessmen?.data.map((x) => (
                              <option value={x.id} key={x.id}>
                                {x.fullname}
                              </option>
                            ))}
                          </Select>
                        </HStack>

                        <HStack>
                          <FormLabel tooltip="Smlouva bude zapsaná pod vybraného správce. V případě, že tyto hodnoty měnit nechcete, ponechejte pole prázdné.">
                            Správce
                          </FormLabel>
                          <Select
                            id="businessmanId"
                            {...register('managerId')}
                            defaultValue={getValues('managerId') ?? ''}
                          >
                            <option value="">Vyberte správce</option>

                            {dataBusinessmen?.data.map((x) => (
                              <option value={x.id} key={x.id}>
                                {x.fullname}
                              </option>
                            ))}
                          </Select>
                        </HStack>
                      </Box>
                    </FormControl>
                  )}

                <FormControl>
                  <FormLabel>Získatelské číslo</FormLabel>
                  {fields.map((field, index) => (
                    <InputGroup
                      key={field.acquisitionNumber}
                      placeholder="Např. 12348765"
                      {...register(`brokerNumbers.${index}.acquisitionNumber`)}
                      isDisabled
                    />
                  ))}
                </FormControl>
              </FormSectionBody>
            )}
          </FormSection>

          {(priceChanged || alerts.length > 0) && (
            <Alert colorScheme="red" mb={8}>
              <Stack direction="column">
                {priceChanged && (
                  <Text>
                    Před sjednáním došlo ke změně ceny.
                    {returnBack && (
                      <>
                        {' '}
                        Vraťte se na{' '}
                        <Link
                          as={RouterLink}
                          colorScheme="blue"
                          to={`/carkulka/wizard/3/${insuranceParamsId!}`}
                        >
                          &nbsp;seznam kalkulací&nbsp;
                        </Link>{' '}
                        a vybranou nabídku překalkulujte.
                      </>
                    )}
                  </Text>
                )}

                {alerts.length > 0 &&
                  alerts
                    .filter((x) => !!x.message)
                    .map((alert) => (
                      <Text
                        key={alert.message}
                        dangerouslySetInnerHTML={{ __html: alert.message ?? '' }}
                      />
                    ))}
              </Stack>
            </Alert>
          )}

          <ErrorSummary errors={errors} />

          {/* {meetingRecord?.errors && meetingRecord.errors.length > 0 &&
            <Alert colorScheme='red' mb={8} justifyContent='end'>
              <VStack >
                {meetingRecord.errors.map(x => <Text>{x.message}</Text>)}
              </VStack>
            </Alert>
          } */}

{ sameAcquirerAndPolicyHolder &&
          <Alert colorScheme='orange' mb={8}>
            Byla detekována shoda poradce s pojistníkem/klientem. Samosjednání pojistné smlouvy není povoleno.
          </Alert>}

          <FormButtons
            leftButtons={
              <Button
                variant="outline"
                as="a"
                href={`${url}/CloneUrl?paramsId=${insuranceParamsId}&replacement=${false}&returnUrl=${encodeURIComponent(`${baseUrl}/${app}/wizard/1`)}`}
                disabled={isSubmitting}
              >
                <Duplicate color="secondary.500" />
                Klonovat
              </Button>
            }
            rightButtons={
              <>
                <Button
                  variant="outline"
                  disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !isValid}
                  isLoading={isSubmittingCurrent && isValid}
                  type="button"
                  onClick={() => {
                    setValue('nextPage', 'current')
                    setIsSubmittingCurrent(true)
                    setAcceptChangedPrice(true)
                  }}
                >
                  Uložit
                </Button>

                {allowCreateContract && (
                  <Button
                    colorScheme="green"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !isValid}
                    isLoading={isSubmittingNext && isValid}
                    loadingText="Sjednávám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      setAcceptChangedPrice(priceChanged)
                    }}
                  >
                    <Check />
                    {!priceChanged ? 'Sjednat pojištění' : 'Sjednat pojištění se změnou ceny'}
                  </Button>
                )}

                <SubmitDialog
                  isOpen={isSavedFormAlertOpen}
                  onClose={savedFormAlertOnClose}
                  header="Uloženo"
                  body={'Údaje byly úspěšně uloženy'}
                />
              </>
            }
          />
        </MainContent>
      </form>
    </FormProvider>
  )
}
